/**
 * Additional indexable copy for priority service pages (scope, timeline, pricing model).
 * Facts only — no invented licence numbers, warranty years, or price ranges.
 */
export type ServiceDetailExpansion = {
  paragraphs: readonly string[];
  extraFaqs?: readonly { question: string; answer: string }[];
};

export const SERVICE_DETAIL_EXPANSIONS: Record<string, ServiceDetailExpansion> = {
  "emergency-roof-leak-repair": {
    paragraphs: [
      "Scope is defined after documentation: you receive a written summary of what was observed, what was stabilized on the first visit when applicable, and what permanent work remains. Revolin Roofing serves Metro Vancouver and the Lower Mainland; urgent dispatch depends on crew availability, safe access and weather.",
      "Pricing follows the assessed scope rather than a flat rate by phone. Emergency stabilization and permanent repair are quoted separately when they cannot be completed in one mobilization.",
      "Typical sequencing moves from interior containment guidance, exterior diagnosis, temporary dry-in if required, then compatible permanent repair. Larger assembly failure may be referred to storm tarping or replacement planning services already published on this site.",
    ],
    extraFaqs: [
      {
        question: "Will you tarp my roof the same day?",
        answer:
          "When scheduling, access and weather allow, temporary dry-in or tarping may be part of the first visit. That decision is made on site after the failure mode is understood.",
      },
      {
        question: "Do you service strata or commercial roofs for leaks?",
        answer:
          "Published low-slope repair and maintenance services cover many non-residential buildings. The same source-first diagnosis applies; access and occupant coordination are scoped per property.",
      },
    ],
  },
  "asphalt-shingle-replacement": {
    paragraphs: [
      "A replacement scope includes tear-off or overlay decisions only after deck and ventilation review. Flashings, valleys, penetrations, edges and ventilation are part of the assembly—not optional add-ons.",
      "Timeline depends on roof area, complexity, material lead times and weather. Expect a written schedule with your scope; rain days may adjust sequencing while keeping exposed areas protected.",
      "Revolin Roofing is a Revolin Group company based in Burnaby. Manufacturer material warranties follow the shingle system selected; workmanship terms are issued in writing with the project.",
    ],
    extraFaqs: [
      {
        question: "Can you match my existing shingle colour?",
        answer:
          "We work toward the closest available match in current product lines. Weathered fields may still show variation at repair boundaries until the new area weathers in.",
      },
      {
        question: "Do you handle detached garages or small structures?",
        answer:
          "Yes, when they are part of the same property assessment. Geometry, access and tie-ins to the main roof are documented in the scope.",
      },
    ],
  },
  "metal-roof-replacement": {
    paragraphs: [
      "Standing-seam and architectural metal systems require precise panel layout, clip or fastening patterns, and transitions at walls, valleys and penetrations. Scope confirms substrate, underlayment and ventilation before panels are ordered.",
      "Metal projects often coordinate fascia, gutters and snow retention when the design calls for them. Those items are listed explicitly rather than assumed.",
      "Lifecycle planning compares initial investment with maintenance expectations. Revolin documents both manufacturer requirements and the workmanship warranty issued for the installation.",
    ],
    extraFaqs: [
      {
        question: "Is metal suitable for every Vancouver home?",
        answer:
          "Not automatically. Slope, structure, coastal exposure and architectural goals all matter. Assessment determines whether metal, shingles or a hybrid approach fits.",
      },
      {
        question: "How noisy is metal roofing?",
        answer:
          "Perceived rain noise depends on the full assembly—deck, insulation, attic and interior finish—not the exterior profile alone.",
      },
    ],
  },
  "flat-roof-replacement-sbs-tpo-pvc": {
    paragraphs: [
      "Low-slope replacement focuses on positive drainage, membrane type (SBS, TPO or PVC as specified), insulation targets and every penetration curb. Parapets and wall transitions are detailed in the scope because they drive most callbacks.",
      "Occupied homes and strata buildings may need phased access plans. Timeline and staging are agreed in writing before mobilization.",
      "Price is assessment-led: square footage alone is not enough when rooftop equipment, slope corrections or tear-off to deck are unknown.",
    ],
    extraFaqs: [
      {
        question: "Can you replace only one section of a flat roof?",
        answer:
          "Sometimes, when transitions to the remaining field can be made watertight and the adjacent membrane is sound. Assessment confirms whether partial replacement is durable.",
      },
      {
        question: "Which membrane should I choose?",
        answer:
          "It depends on slope, use, equipment, desired maintenance and manufacturer requirements. We explain trade-offs after inspection rather than defaulting to one product.",
      },
    ],
  },
  "cedar-shake-roof-replacement": {
    paragraphs: [
      "Cedar replacement respects coursing, keyways, ridge and hip details, and ventilation paths that keep the shakes drying evenly. Scope states whether existing skip-sheathing or solid deck conditions require correction.",
      "Fire-retardant or treatment requirements, where applicable, are confirmed against local rules for the property address before materials are ordered.",
      "Maintenance expectations after replacement—including moss control and gutter flow—are part of the handover conversation for West Coast exposure.",
    ],
    extraFaqs: [
      {
        question: "Can you replace cedar in winter?",
        answer:
          "Weather and moisture management determine safe installation windows. We schedule to protect exposed decks and maintain manufacturer installation conditions.",
      },
      {
        question: "Is repair ever enough instead of full replacement?",
        answer:
          "Localized repair may suit isolated damage. Widespread deterioration, repeated leaks or poor underlying ventilation often points toward full replacement instead.",
      },
    ],
  },
  "roof-health-check-maintenance": {
    paragraphs: [
      "Maintenance visits document drainage paths, flashings, penetrations, ventilation intake and exhaust, and early wear that could become leaks. You receive findings you can use for budgeting—not a generic pass/fail sticker.",
      "Moss treatment, minor clearing and gutter coordination may be recommended as linked services when they affect roof performance. Each item is scoped separately if work beyond the inspection is requested.",
      "Annual or storm-follow-up checks are common in Metro Vancouver. Scheduling is planned; this is not the same as emergency leak dispatch unless active entry is occurring.",
    ],
    extraFaqs: [
      {
        question: "Does a maintenance visit include cleaning?",
        answer:
          "Inspection and documentation are the core. Cleaning, moss removal or gutter flushing are separate services when you choose to add them.",
      },
      {
        question: "Can strata councils use the report?",
        answer:
          "Yes. Findings are written for decision-makers and can support capital planning alongside the strata roofing pathway on this site.",
      },
    ],
  },
  "gutter-replacement": {
    paragraphs: [
      "Gutter replacement scope covers size, slope, outlet placement, fascia condition and downspout discharge away from foundations. It is coordinated with roof edge metal when both are failing.",
      "Seamless aluminum is common on residential slopes; colour and profile are selected to match trim already on the home unless a broader repaint is planned.",
      "Related published services include gutter cleaning, guards and fascia repair when those details affect long-term performance.",
    ],
    extraFaqs: [
      {
        question: "Should gutters be replaced before or after re-roofing?",
        answer:
          "Often during or immediately after roof work so edge metal and gutters lap correctly. Assessment clarifies the safest sequence for your property.",
      },
      {
        question: "Do you repair sectional gutters?",
        answer:
          "Localized repairs may be possible. When hangers, slope or seams are failing across a run, replacement is usually more reliable.",
      },
    ],
  },
};
